import { stopBootMark } from "./boot-overlay";
import { t } from "./contract/strings";

/**
 * The boot-path fault panel: the collapsed `Technical details` disclosure and the
 * `Reload` link that sit under the boot text while the boot overlay is still up (#R11).
 *
 * The elements are created here, so this module owns the reference rather than reaching
 * back into the shell for `#boot-fault-detail` -- an id `index.html` deliberately does not
 * declare, because the paragraph is not part of the static document and looking a
 * created-by-JS id up out of the shell is the exact half-a-rename the DOM probe in
 * `tests/web_dom_probe.mjs` exists to catch. `boot` and `#boot-text` stay as a probe
 * contract with `index.html`; this panel does not.
 *
 * Holding the elements at module scope also gives the right answer to a second error:
 * the same paragraph is updated in place, and no second disclosure or reload link
 * appears. If something has since detached the panel from the document (a boot retry
 * that reset the overlay), it is re-attached before the text is written.
 */

let bootFault:
  | { detail: HTMLParagraphElement; panel: HTMLDetailsElement; reload: HTMLAnchorElement }
  | null = null;

/**
 * Put (or update) the diagnostic text under the boot text, `text`, while the overlay
 * is up. Returns nothing; the panel carries `#boot-fault-detail` as its identity.
 */
export function showBootFault(text: Element, message: string): void {
  // Boot has stopped: the mark holds still beside a Reload, as on every boot failure (#1066).
  stopBootMark(text.parentElement);
  if (!bootFault) {
    const panel = document.createElement("details");
    const summary = document.createElement("summary");
    summary.textContent = t("common.technical_details");
    const detail = document.createElement("p");
    detail.id = "boot-fault-detail";
    panel.append(summary, detail);
    const reload = document.createElement("a");
    reload.href = "/";
    reload.textContent = t("app.reload");
    text.after(reload, panel);
    bootFault = { detail, panel, reload };
  } else if (!bootFault.detail.isConnected) {
    text.after(bootFault.reload, bootFault.panel);
  }
  bootFault.detail.textContent = message;
}